import React from 'react'

import { Box, Stack, Typography, alpha } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'

import { PRIMARY_NAV_ITEMS, showsPrimaryNav, tabNavState } from 'components/UI/primaryNav'
import { font, space } from 'settings/tokens'
import { APP_MAX_WIDTH } from 'constants/layout'
import { glassSx } from 'utils/glass'

/** Bar height excluding the safe-area inset. */
export const BOTTOM_NAV_HEIGHT = 56

/** Gap between the floating bar and the screen edges. */
export const BOTTOM_NAV_INSET = 16

/**
 * Total vertical space the bar occupies, for the layout to reserve.
 *
 * A FLOATING bar needs more than its own height: it also clears the bottom
 * inset, and it is translucent, so content that stops exactly at its top edge
 * is still legible THROUGH it and reads as clipped. The extra inset above
 * gives the last row somewhere to end.
 */
export const BOTTOM_NAV_RESERVED = BOTTOM_NAV_HEIGHT + BOTTOM_NAV_INSET * 2

/*
 * The destination list, the slide-direction rule and the "does the nav show here"
 * predicate all moved to `components/UI/primaryNav` — they are properties of the
 * NAV, and this file is only one of its two presentations (`SideNav` is the other).
 *
 * They lived here, and living here is what caused the bug: this component is
 * `display: { xs: 'flex', md: 'none' }`, so desktop could not read the list and
 * grew a second, hand-maintained one in the certificates page's hamburger. The two
 * disagreed, and Certificates and Activity ended up unreachable at desktop width.
 *
 * Re-exported rather than repointed at every call site: `layouts/Default` and
 * Profile's "My certificates" back-link both import from here, and a rename would
 * be churn in files this change has no other reason to touch. New code should
 * import from `primaryNav` directly.
 */
export { tabNavState } from 'components/UI/primaryNav'

/** @deprecated Prefer `showsPrimaryNav` from `components/UI/primaryNav`. */
export const showsBottomNav = showsPrimaryNav

/**
 * Primary navigation, pinned to the bottom, MOBILE ONLY.
 *
 * Replaces the hamburger as the way between the app's top-level destinations.
 * The reference apps this surface is measured against — Chrono24, Artsy, eBay —
 * all put primary navigation at the bottom on a phone and leave the top for
 * search and filters, and the reason is reach: the top-right corner of a
 * 6.1-inch phone is the hardest place on the screen for a thumb, which is
 * exactly where a hamburger puts every destination.
 *
 * Desktop renders `SideNav` instead — the SAME destinations from `primaryNav`, as
 * a left rail. A bar pinned across a 1280px viewport reads as a phone app running
 * full-screen, and a pointer has no reach cost to solve, so the presentation
 * differs while the list does not. Exactly one of the two renders per breakpoint,
 * so only one element ever claims `aria-label='Primary'`.
 *
 * ## The safe-area inset is not optional
 *
 * On any iPhone without a home button, the bottom ~34px is the home-indicator
 * region. A bar that stops at `bottom: 0` puts its labels under that indicator
 * and its tap targets in the swipe-up gesture zone. `env(safe-area-inset-bottom)`
 * is added to the padding, not the height, so the bar's CONTENT stays 56px and
 * only its background extends into the inset. On a device without an inset the
 * value resolves to 0 and nothing changes.
 */
export const BottomNav: React.FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  /*
   * A tab switch travels in the direction the tab sits, so the bar reads as a map
   * of where the pages are rather than three unrelated destinations. Certificates
   * is leftmost, so arriving there always comes from the left; Profile is
   * rightmost, so it always comes from the right; Activity, in the middle, is
   * genuinely bidirectional — which is why this is computed from the index delta
   * and not assigned per tab.
   *
   * The direction rides in router state and the layout plays it, because the
   * incoming page is what animates and it does not exist yet at this point.
   */
  const goToTab = (to: string) => {
    if (to === pathname) return
    navigate(to, { state: tabNavState(pathname, to) })
  }

  if (!showsPrimaryNav(pathname)) return null

  return (
    <Stack
      component='nav'
      aria-label='Primary'
      direction='row'
      sx={theme => ({
        display: { xs: 'flex', md: 'none' },
        position: 'fixed',
        /*
          Clamped to the same column as the content (constants/layout).

          `position: fixed` resolves against the VIEWPORT, not the centred content,
          so past the ceiling the bar would stretch the full window while the pages
          it navigates sat centred — the nav and its content disagreeing about where
          the app is. `left/right: INSET` keeps the phone behaviour untouched (the
          clamp is inert at any width this bar actually shows at, since it is
          mobile-only), and `margin: '0 auto'` centres it if that ever changes.
        */
        maxWidth: `${APP_MAX_WIDTH}px`,
        margin: '0 auto',
        // FLOATING, not edge-to-edge: inset on all three sides so the page
        // scrolls visibly past it and the bar reads as an object above the
        // content rather than as the bottom of the window.
        left: BOTTOM_NAV_INSET,
        right: BOTTOM_NAV_INSET,
        bottom: `calc(${BOTTOM_NAV_INSET}px + env(safe-area-inset-bottom, 0px))`,
        /*
          1100 (MUI's appBar level), NOT 1200.
          
          1200 is MUI's DRAWER level, so the nav and every bottom sheet in the
          app sat on the same layer and DOM order decided the winner — the nav
          rendered last and covered the Display sheet's own content. The nav is
          page chrome; a sheet is a modal surface and must be above it.
        */
        zIndex: 1100,
        height: BOTTOM_NAV_HEIGHT,
        overflow: 'hidden',

        /*
         * The "liquid glass" material: a translucent fill that SAMPLES what is
         * behind it, rather than a flat panel that hides it.
         *
         * `saturate` is the part that is easy to omit and the part that does the
         * work. Blur alone greys out whatever is underneath, so the bar looks
         * like frosted plastic; pushing saturation back up keeps the colour of
         * the content showing through, which is what reads as glass. Apple's own
         * material is a blur plus a saturation boost for exactly this reason.
         *
         * `-webkit-` prefix included: Safari — i.e. every iPhone, the device
         * this app is actually opened on — still needs it.
         */
        // Was 0.72 here and 0.82 on every other bar — the drift the token exists to
        // stop. Now the shared value.
        ...glassSx(theme),

        /*
         * Radius from the tenant's Studio token, per the brand contract — the
         * same value buttons take verbatim. A brand that chose square gets a
         * square floating bar, which is coherent with the rest of its UI.
         */
        borderRadius: `${theme.shape.borderRadius}px`,

        // Hairline + lift. Both are needed: on a light page the border is what
        // separates glass from paper, and the shadow is what says it is above.
        border: `1px solid ${alpha(theme.palette.common.black, 0.06)} !important`,
        boxShadow: '0 8px 32px rgba(16,24,40,.16), 0 2px 8px rgba(16,24,40,.08)',

        /*
         * Degrade honestly where the material is unsupported (older Firefox, or
         * a browser with backdrop-filter disabled): fall back to a nearly-opaque
         * fill rather than leaving a 72%-transparent bar with unreadable labels
         * over scrolling content.
         */

      })}
    >
      {PRIMARY_NAV_ITEMS.map(({ label, icon: Icon, to, match }) => {
        const active = match(pathname)
        return (
          <Box
            key={to}
            /*
              `link` + `aria-current`, NOT `tab` + `aria-selected`.

              Two things were wrong. The container is `component='nav'` with no
              `role='tablist'` anywhere, and a `tab` must be OWNED by a `tablist` —
              so the role was invalid and a reader may ignore or mis-announce it.
              And these rows navigate to routes; they do not switch panels within a
              view, which is what the tab pattern describes.

              It also drifted from `SideNav`, which announces the active row with
              `aria-current='page'`. The two are the same destination list rendered
              at two breakpoints (`components/UI/primaryNav`), and that file exists
              precisely so the two presentations cannot diverge — an a11y
              divergence is still a divergence.
            */
            role='link'
            aria-current={active ? 'page' : undefined}
            aria-label={label}
            tabIndex={0}
            onClick={() => goToTab(to)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                goToTab(to)
              }
            }}
            sx={theme => ({
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: `${space.xs}px`,
              cursor: 'pointer',
              // The whole cell is the target, so the tap area is ~125x56 rather
              // than the glyph's 22px.
              height: BOTTOM_NAV_HEIGHT,
              color: active ? theme.palette.text.primary : theme.palette.text.secondary,
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.text.primary}`,
                outlineOffset: '-2px',
              },
            })}
          >
            {/* Weight carries the active state alongside colour. Colour alone
                fails for the ~8% of men with a colour-vision deficiency, and
                this is the app's only persistent orientation cue. */}
            <Icon size={22} strokeWidth={active ? 2 : 1.5} />
            <Typography
              sx={{
                fontSize: `${font.label - 2}px`,
                fontWeight: active ? 600 : 400,
                lineHeight: 1,
                letterSpacing: '-0.01em',
              }}
            >
              {label}
            </Typography>
          </Box>
        )
      })}
    </Stack>
  )
}

export default BottomNav
